import React, { useEffect, useState } from 'react';
import { Box, Button, Modal, Radio, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const boxStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '460px',
  maxHeight: '80vh',
  bgcolor: '#fff',
  borderRadius: '10px',
  border: 'none',
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
};

const PRIMARY = '#0F7F85';

// 행 식별 키. COURSE는 memberCourseTicketId, TRIAL은 memberTrialTicketId가 null이 아니므로
// ticketType + 해당 id 조합으로 유니크하게 만든다(둘 다 단일 id로만 구분하면 TRIAL끼리 null 충돌).
const ticketKey = ticket =>
  ticket.ticketType === 'TRIAL'
    ? `TRIAL-${ticket.memberTrialTicketId}`
    : `COURSE-${ticket.memberCourseTicketId}`;

// 보유 수강권/일일권이 2개 이상일 때 차감할 티켓을 직원이 고르는 모달
export default function SelectTicketModal({ open, onClose, memberName, tickets = [], onConfirm }) {
  const [selectedKey, setSelectedKey] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedKey(tickets?.length ? ticketKey(tickets[0]) : null);
    }
  }, [open, tickets]);

  const selectedTicket = tickets.find(ticket => ticketKey(ticket) === selectedKey) ?? null;

  const formatCount = ticket => {
    if (ticket.unlimitedYn) {
      return '무제한';
    }
    const remaining = ticket.remainingCount ?? 0;
    const total = ticket.totalCount ?? 0;
    return `잔여 ${remaining} / ${total}회`;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={boxStyle}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#111',
            color: '#fff',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
            p: '16px 20px',
          }}>
          <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>차감할 수강권 선택</Typography>
          <CloseIcon sx={{ cursor: 'pointer' }} onClick={onClose} />
        </Box>

        <Box sx={{ p: '16px 20px', overflowY: 'auto' }}>
          <Typography sx={{ fontSize: '13px', color: '#666', mb: '12px' }}>
            {memberName ? `${memberName} 회원이 ` : ''}이 수업에 사용할 수 있는 수강권이 여러 개입니다.
            예약에 차감할 수강권을 선택하세요.
          </Typography>

          {tickets.map(ticket => {
            const key = ticketKey(ticket);
            const checked = selectedKey === key;
            return (
              <Box
                key={key}
                onClick={() => setSelectedKey(key)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  p: '12px',
                  mb: '8px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  border: checked ? `1.5px solid ${PRIMARY}` : '1px solid #e1e1e1',
                  background: checked ? '#F0F8F8' : '#fff',
                }}>
                <Radio
                  checked={checked}
                  size="small"
                  sx={{ p: 0, color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                      {ticket.courseTicketName}
                    </Typography>
                    {ticket.ticketType === 'TRIAL' && (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: '#fff',
                          background: PRIMARY,
                          borderRadius: '4px',
                          px: '6px',
                        }}>
                        일일권
                      </Typography>
                    )}
                    {ticket.status === 'STOPPED' && (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: '#fff',
                          background: '#9A9A9A',
                          borderRadius: '4px',
                          px: '6px',
                        }}>
                        정지
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '12px', color: '#888', mt: '2px' }}>
                    {ticket.startDate} ~ {ticket.expireDate} · {formatCount(ticket)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', gap: '8px', p: '16px 20px', borderTop: '1px solid #eee' }}>
          <Button
            fullWidth
            onClick={onClose}
            sx={{
              height: '40px',
              color: '#707070',
              background: '#fff',
              borderRadius: '10px',
              border: '1px solid #707070',
            }}>
            취소
          </Button>
          <Button
            fullWidth
            disabled={selectedTicket == null}
            onClick={() => onConfirm(selectedTicket)}
            sx={{
              height: '40px',
              color: '#fff',
              background: PRIMARY,
              borderRadius: '10px',
              '&:hover': { backgroundColor: PRIMARY },
              '&.Mui-disabled': { background: '#ccc', color: '#fff' },
            }}>
            선택 완료
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
